import React, { useState, useEffect, useRef } from "react";
import "./CommandPalette.css";
import { useNavigate, useLocation } from "react-router-dom";
import postsData from "../../posts/posts.json";

export const CommandPalette = ({ isOpen, onClose, onZenToggle, onShowCat }) => {
    const [input, setInput] = useState("");
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [currentMenu, setCurrentMenu] = useState(null); // null = main, "goto" = goto menu
    const inputRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    const mainCommands = [
        {
            name: "goto",
            description: "Go to a specific page or post",
            execute: openGotoMenu,
        },
        {
            name: "print",
            description: "Export a printable version of the current page",
            execute: exportPDF,
        },
        {
            name: "link",
            description: "Copy current page link to clipboard",
            execute: copyLink,
        },
        {
            name: "random",
            description: "Go to a random post",
            execute: goToRandomPost,
        },
        {
            name: "zen",
            description: "Toggle zen mode (hide UI)",
            execute: () => {
                if (onZenToggle) {
                    onZenToggle();
                }
                onClose();
            },
        },
        {
            name: "cat",
            description: "Spawn a cat that follows your mouse",
            execute: () => {
                if (onShowCat) {
                    onShowCat();
                }
                onClose();
            },
        },
    ];

    // Build goto menu from available pages
    const gotoItems = [
        { name: "Home", path: "/" },
        { name: "Posts", path: "/posts" },
        { name: "Tools", path: "https://tools.mustardtiger.xyz" },
        ...postsData
            .filter((post) => {
                const now = new Date();
                if (post.publishAt === null || post.publishAt === undefined) {
                    return true;
                }
                return new Date(post.publishAt) <= now;
            })
            .map((post) => ({
                name: post.title,
                path: `/posts/${post.id}`,
            })),
    ];

    const gotoMenu = gotoItems.map((item) => ({
        name: item.name.toLowerCase().replace(/\s+/g, "-"),
        displayName: item.name,
        description: `Go to ${item.name}`,
        path: item.path,
        execute: () => {
            if (
                item.path.startsWith("http://") ||
                item.path.startsWith("https://")
            ) {
                window.location.href = item.path;
            } else {
                navigate(item.path);
            }
            onClose();
        },
    }));

    const commands = currentMenu === "goto" ? gotoMenu : mainCommands;

    function openGotoMenu() {
        setCurrentMenu("goto");
        setInput("");
        setSelectedIndex(0);
    }

    function exportPDF() {
        // Get the current page content
        const mainContent = document.querySelector(".main-content");
        if (!mainContent) {
            console.error("Could not find main content");
            return;
        }

        // Create a new window for printing
        const printWindow = window.open("", "", "width=800,height=600");
        if (!printWindow) {
            console.error("Could not open print window");
            return;
        }

        // Get page title for filename
        const pageTitle = document.querySelector("h1")?.textContent || "page";
        const filename = pageTitle.toLowerCase().replace(/\s+/g, "-") + ".pdf";

        // Clone the content and style it for printing
        const content = mainContent.cloneNode(true);

        // Build HTML for print
        const printHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>${pageTitle}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }

            body {
              font-family: 'Courier New', monospace;
              line-height: 1.6;
              color: #fff;
              background: #0a0e27;
              padding: 40px;
            }

            h1, h2, h3, h4, h5, h6 {
              margin-top: 1.5em;
              margin-bottom: 0.5em;
              font-weight: bold;
            }

            h1 { font-size: 2em; }
            h2 { font-size: 1.5em; }
            h3 { font-size: 1.2em; }

            p {
              margin-bottom: 1em;
            }

            ul, ol {
              margin-left: 2em;
              margin-bottom: 1em;
            }

            li {
              margin-bottom: 0.5em;
            }

            code, pre {
              background: #1a1f3a;
              padding: 0.2em 0.4em;
              border-radius: 3px;
              font-family: 'Courier New', monospace;
              color: #a0d995;
              font-size: 0.9em;
            }

            pre {
              padding: 1em;
              overflow-x: auto;
              margin: 1em 0;
            }

            pre code {
              padding: 0;
              background: none;
              color: inherit;
            }

            blockquote {
              border-left: 4px solid #555;
              padding-left: 1em;
              margin: 1em 0;
              color: #aaa;
            }

            a {
              color: #00d4ff;
              text-decoration: none;
            }

            a:hover {
              text-decoration: underline;
            }

            img {
              max-width: 100%;
              height: auto;
              margin: 1em 0;
            }

            table {
              border-collapse: collapse;
              width: 100%;
              margin: 1em 0;
            }

            th, td {
              border: 1px solid #555;
              padding: 0.5em;
              text-align: left;
            }

            th {
              background: #1a1f3a;
              font-weight: bold;
            }

            .tag {
              display: inline-block;
              background: #1a1f3a;
              color: #00d4ff;
              padding: 0.2em 0.5em;
              border-radius: 3px;
              margin-right: 0.5em;
              font-size: 0.9em;
            }

            .post-meta {
              color: #888;
              font-size: 0.9em;
              margin-bottom: 1em;
            }

            .post-author {
              color: #888;
            }

            @media print {
              body {
                padding: 20px;
              }

              page-break-inside: avoid;

              h1, h2, h3 {
                page-break-after: avoid;
              }

              ul, ol, p {
                page-break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          ${content.innerHTML}
        </body>
      </html>
    `;

        printWindow.document.write(printHTML);
        printWindow.document.close();

        // Wait for content to load, then print
        setTimeout(() => {
            printWindow.print();
            printWindow.close();
        }, 250);

        onClose();
    }

    function copyLink() {
        const url = window.location.href;
        navigator.clipboard
            .writeText(url)
            .then(() => {
                // Show feedback that link was copied
                console.log("Link copied to clipboard");
            })
            .catch((err) => {
                console.error("Failed to copy link:", err);
            });
        onClose();
    }

    function goToRandomPost() {
        // Filter published posts
        const now = new Date();
        const publishedPosts = postsData.filter((post) => {
            if (post.publishAt === null || post.publishAt === undefined) {
                return true;
            }
            return new Date(post.publishAt) <= now;
        });

        if (publishedPosts.length === 0) {
            console.error("No published posts available");
            return;
        }

        const randomPost =
            publishedPosts[Math.floor(Math.random() * publishedPosts.length)];
        navigate(`/posts/${randomPost.id}`);
        onClose();
    }

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isOpen) return;

            if (e.key === "Escape") {
                e.preventDefault();
                if (currentMenu === "goto") {
                    setCurrentMenu(null);
                    setInput("");
                    setSelectedIndex(0);
                } else {
                    onClose();
                }
            } else if (e.key === "Enter") {
                e.preventDefault();
                // Filter commands the same way as the render does
                const currentFiltered = commands.filter((cmd) =>
                    cmd.name.includes(input.toLowerCase()),
                );
                // Use the visually selected item from filtered commands
                if (
                    selectedIndex >= 0 &&
                    selectedIndex < currentFiltered.length
                ) {
                    currentFiltered[selectedIndex].execute();
                    setInput("");
                }
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setSelectedIndex(Math.max(0, selectedIndex - 1));
            } else if (e.key === "ArrowDown") {
                e.preventDefault();
                setSelectedIndex(
                    Math.min(commands.length - 1, selectedIndex + 1),
                );
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, input, selectedIndex, onClose, currentMenu, commands]);

    const filteredCommands = commands.filter((cmd) =>
        cmd.name.includes(input.toLowerCase()),
    );

    if (!isOpen) return null;

    return (
        <div className="command-palette-overlay" onClick={onClose}>
            <div
                className="command-palette"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="command-input-wrapper">
                    {currentMenu === "goto" && (
                        <span className="command-breadcrumb">:goto / </span>
                    )}
                    <span className="command-prompt">:</span>
                    <input
                        ref={inputRef}
                        type="text"
                        className="command-input"
                        value={input}
                        onChange={(e) => {
                            setInput(e.target.value);
                            setSelectedIndex(0);
                        }}
                        placeholder={
                            currentMenu === "goto"
                                ? "Search pages..."
                                : "Enter command..."
                        }
                    />
                </div>

                {filteredCommands.length > 0 && (
                    <div className="command-list">
                        {filteredCommands.map((cmd, idx) => (
                            <div
                                key={cmd.name}
                                className={`command-item ${selectedIndex === idx ? "selected" : ""}`}
                                onClick={() => {
                                    cmd.execute();
                                    setInput("");
                                }}
                            >
                                <div className="command-name">
                                    {cmd.displayName || cmd.name}
                                </div>
                                <div className="command-description">
                                    {cmd.description}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {input && filteredCommands.length === 0 && (
                    <div className="command-no-match">
                        <p>
                            No {currentMenu === "goto" ? "pages" : "commands"}{" "}
                            found: "{input}"
                        </p>
                        {currentMenu === null && (
                            <p className="hint">
                                Available commands: home, posts, goto, print,
                                link, random, zen, cat
                            </p>
                        )}
                    </div>
                )}

                {currentMenu === "goto" && (
                    <div className="command-hint">
                        <p>Press ESC to go back</p>
                    </div>
                )}
            </div>
        </div>
    );
};
